import { SectionHeading } from "@/components/shared/SectionHeading";
import { Reveal, RevealStagger, RevealItem } from "@/components/shared/Reveal";
import { FileText, Wallet, ShieldCheck, CheckCircle2, ArrowRight } from "lucide-react";

const FUNDING_FLOW = ["Assignment Created", "Payment Funded", "Funds Secured", "Driver Accepts", "Assignment Completed", "Delivery Verified", "Payment Released"];
const INSURANCE_CAPS = ["Coverage verification", "Trip specific coverage workflows", "Insurance documentation", "Claims reporting", "Accident evidence", "Cargo claims", "Vehicle damage", "Adjuster communication", "Claim status"];

export const PaymentTrust = () => {
  return (
    <section data-testid="payment-trust-section" className="py-24 md:py-32 bg-rr-surface">
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <SectionHeading eyebrow="Payment Protection" title="Know the Funds Are There." testId="payment-protection" />

        <Reveal delay={0.2}>
          <div className="mt-10 flex flex-col md:flex-row items-center gap-1 overflow-x-auto pb-2">
            {FUNDING_FLOW.map((step, i) => (
              <div key={step} className="flex items-center gap-1 shrink-0">
                <div data-testid={`funding-step-${i}`} className="bg-white border border-rr-line rounded-full px-4 py-2.5 text-xs font-bold text-rr-navy whitespace-nowrap">
                  {step}
                </div>
                {i < FUNDING_FLOW.length - 1 && <ArrowRight size={14} className="text-rr-orange shrink-0" />}
              </div>
            ))}
          </div>
        </Reveal>

        <Reveal delay={0.3}>
          <div className="mt-8 inline-flex items-center gap-2 bg-rr-green/10 text-rr-green font-bold text-sm px-4 py-2.5 rounded-full">
            <CheckCircle2 size={16} /> FUNDS SECURED — visible to drivers before work begins
          </div>
          <p className="mt-4 text-sm text-rr-muted max-w-2xl">
            RelayRigs is designed to distinguish between RelayRigs subscription/platform revenue and funds designated for driver compensation. RelayRigs does not promise legally regulated "escrow" unless the final payment structure and provider support that terminology.
          </p>
        </Reveal>

        <div className="mt-20 grid lg:grid-cols-[0.9fr_1.1fr] gap-14 items-start">
          <div>
            <SectionHeading eyebrow="Insurance Is Required" title="Insurance Requirements Before You Register." testId="insurance" />
            <div data-testid="insurance-disclaimer" className="mt-6 bg-white border border-rr-orange/30 rounded-xl p-5">
              <p className="text-sm text-rr-navy font-semibold leading-relaxed">
                Participants must maintain applicable insurance required for their vehicle, operation, assignment, and jurisdiction.
              </p>
              <p className="text-xs text-rr-muted mt-2.5 leading-relaxed">
                RelayRigs does not automatically provide insurance simply because a user joins the marketplace.
              </p>
            </div>
          </div>
          <RevealStagger className="grid sm:grid-cols-3 gap-3">
            {INSURANCE_CAPS.map((c) => (
              <RevealItem key={c}>
                <div data-testid={`insurance-cap-${c.toLowerCase().replace(/[^a-z]+/g, "-")}`} className="bg-white border border-rr-line rounded-lg px-4 py-3 text-sm font-semibold text-rr-navy flex items-center gap-2">
                  <ShieldCheck size={15} className="text-rr-orange shrink-0" /> {c}
                </div>
              </RevealItem>
            ))}
          </RevealStagger>
        </div>
        <p className="text-xs text-rr-muted mt-6 max-w-2xl">
          RelayRigs is not presented as an insurance company unless it becomes appropriately licensed. Verification supports integration with licensed insurance partners where available.
        </p>
      </div>
    </section>
  );
};
